<template>
  <div v-if="theModel">
    <!-- all charts have this -->
    <div class="row no-wrap full-width" style="gap: 4px;">
      <MostaSelect
        v-model="widthModel"
        class="q-mb-xs"
        style="flex: 1 1 0; min-width: 0;"
        :options="widthOptions"
      />

      <MostaSelect
        v-model="elementWidthModel"
        v-show="scaleLinear"
        class="q-mb-xs"
        style="flex: 1 1 0; min-width: 0;"
        :options="elementWidthOptions"
      />
    </div>
    
    <div :class="[{ 'text-red cursor-pointer': stepIncorrect }, 'text-h8 text-bold q-mt-md']">
      {{ $t('x_step') }}
      <q-tooltip :delay="300" anchor="top middle" self="center middle" v-if="stepIncorrect">
        {{ stepIncorrect }}
      </q-tooltip>
    </div>
    <div class="row no-wrap">
      <q-input
        v-model.number="stepValueModel"
        debounce="400"
        type="number"
        dense
        class="thin-input q-mr-sm"
        outlined
        @keydown.enter.stop.prevent
      />
      <enum-display-list :elements="linearStepToList" :enum-to-string="LinearStepToStr" 
          :allow-multiple="false" :combobox="true" :element-type="8" v-model="stepModel"/>
    </div>
    <div :class="[{ 'text-red cursor-pointer': datesIncorrect }, 'text-h8 text-bold q-mt-md']">
      {{ $t('range') }}
      <q-tooltip :delay="300" anchor="top middle" self="center middle" v-if="datesIncorrect">
        {{ datesIncorrect }}
      </q-tooltip>
    </div>
    <div class="row full-width">
      <date-range-display-item v-model="dateInnerModel" :modelType="2" 
        :show-time="genstore.selectedItem.DestType === 6 && stepModel.includes(5)" />
    </div>

    <!-- person fact flags -->
    <div v-if="(theModel as PersonLinearChartSettings)?.Descriptor === 12">
      <q-select
        flat
        v-model="factFlagsModelPerson"
        hide-bottom-space
        multiple
        :bottom-slots="false"
        dense outlined
        clearable
        input-debounce="500"
        :label="factFlagsModelPerson ? $t('contacts_count_in_set') : ''"
        ref="facts_status_select"
        :hide-dropdown-icon="false"
        :options="factFlagsOptionsPerson"
      >
        <template v-slot:no-option>
          <q-item>
            <q-item-section class="text-grey">
              {{ $t('no_data_found') }}
            </q-item-section>
          </q-item>
        </template>      
      </q-select>
    </div>

    <!-- file fact flags -->
    <div v-if="(theModel as PersonLinearChartSettings)?.Descriptor === 9">
      <q-select
        flat
        v-model="factFlagsModelFile"
        hide-bottom-space
        multiple
        :bottom-slots="false"
        dense outlined
        clearable
        input-debounce="500"
        :label="factFlagsModelFile ? $t('files_count_in_set') : ''"
        ref="facts_status_select"
        :hide-dropdown-icon="false"
        :options="factFlagsOptionsFile"
      >
        <template v-slot:no-option>
          <q-item>
            <q-item-section class="text-grey">
              {{ $t('no_data_found') }}
            </q-item-section>
          </q-item>
        </template>      
      </q-select>
    </div>

    <element-options-settings-panel
      v-if="hasAnalyticElementOptions"
      v-model="theModel.Options"
    />

    <div class="row justify-left full-width q-py-xs no-wrap" style="margin-left: -5px !important;">
      <q-btn
        class="q-my-none q-pa-none text-left text-md" flat no-caps align="left" size="13px" color="grey-8" 
        icon="add"
        @click="addNewGElement"
        :aria-label="$t('add_curve')"
      >
      </q-btn>
      <div class="text-h8 q-ml-sm q-mb-none" style="margin-top: 7px; font-weight: 500;">
        {{ $t('curves') }}
      </div>
    </div>

    <div v-if="curveModel">
      <q-chip v-for="(curve, index) in curveModel"
        removable
        square
        icon-selected="none"
        icon-remove="delete"
        :key="curve.Id"
        :selected="selectedId === curve.Id"
        @click="() => { selectedId = curve.Id; tempName = curve.Name; }" 
        :style="`background-color: ${curve_index === index ? '#F0F0F0' : '#FFFFFF'};`"
        text-color="grey-9"
        class="cursor-pointer full-width no-icon-chip"
        @remove="deleteGElement(index)"
      >
        <template v-slot:default>
          <div class="row justify-center no-wrap full-width">
            <div class="q-mr-xs" style="overflow-x: hidden; max-width: 80% !important;">
              {{ curve.Name }}
            </div>
            <q-btn
              class="q-my-none q-mx-xs q-pa-none"
              flat
              dense
              color="grey-7"
              :aria-label="$t('rename')"
              icon="edit"
              size="xs"
              @click.stop.prevent
              @mousedown.stop.prevent
              @mouseup="(e: any) => { renameBtnClick(index) }"
            >
              <q-tooltip :delay="300" anchor="top middle" self="center middle">
                {{ $t('rename') }}
              </q-tooltip>
              <!-- v-model="isEditingMode" -->
              <!-- @keydown.esc="isEditingMode = false" -->
              <q-menu fit style="max-width: 400px;"
                anchor="bottom end" self="top middle" :ref="(el: any) => { menus[index] = el }">
                <div class="row justify-center q-ma-xs">
                  <q-input autogrow autofocus color="white" :ref="(el: any) => { inputs[index] = el }"
                    debounce="500" multiline dense outlined hide-bottom-space
                    v-model="tempName" no-error-icon :placeholder="$t('name')"
                    :rules="[(val: string) => !!val || $t('input_name')]"
                    @keydown.enter.stop.prevent="(val: any) => { renameGElement(index); }"
                  />
                  <q-item-section>
                    <q-btn
                      class="q-my-none q-ml-xs q-pa-none unset-items-center"
                      flat
                      dense
                      round
                      color="grey-7"
                      bg-color="transparent"
                      @click="renameGElement(index)"
                      :aria-label="$t('apply_changes')"
                      icon="done"
                      size="sm"
                      v-close-popup
                    >
                      <q-tooltip :delay="300" anchor="top middle" self="center middle">
                        {{ $t('apply_changes') }}
                      </q-tooltip>
                    </q-btn>
                  </q-item-section>
                </div>
              </q-menu>
            </q-btn>
            <q-space/>
            <q-btn
              class="q-my-none q-mx-xs q-pa-none"
              flat
              dense
              color="grey-7"
              :aria-label="$t('clone')"
              icon="content_copy"
              size="xs"
              @click.stop.prevent="cloneGElement(index)"
            >
              <q-tooltip :delay="300" anchor="top middle" self="center middle">
                {{ $t('clone') }}
              </q-tooltip>
            </q-btn>
          </div>
        </template>
      </q-chip>
      <div v-if="curveModel && curveModel.length && curve_index >= 0 && curve_index < curveModel.length">
        <div class="text-h8 text-bold q-mt-md">
          {{ $t('curve_settings') }}
        </div>

        <div v-if="!isEvaluateFormulas"
        class="text-h8 text-bold q-mt-md">
          {{ $t('evaluate_function') }}
        </div>
        <enum-display-list v-if="!isEvaluateFormulas"
        :elements="linearFunctionToList" :enum-to-string="LinearFunctionToStr" 
        :allow-multiple="false" :combobox="false" :element-type="8" v-model="curveLinearModel"/>

        <div v-if="isLinearChart" 
          class="text-h8 text-bold q-mt-md q-mb-none"
        >
          {{ $t('func_label') }}
        </div>
        <enum-display-list v-if="isLinearChart"
          :elements="evaluateToList" :enum-to-string="EvaluateFunctionToStr" 
          :allow-multiple="false" :combobox="false" :element-type="8" v-model="curveEvaluateModel"/>

        <div class="q-my-xs"
        v-if="isLinearChart && isEvaluateFormulas && !isReportSegment"
        >
          <formulas-list
            :use-chart-mode="true" :parameter-options="graphstore.getCurrentFormulas"
            nested-key-name="ChildFormulas" template-name="Entity"
            v-model="(curveModel[curve_index] as ClientProjectLinearCurveTypes).Formulas"
            @changePosition="moveFormulaItem"
          />
        </div>
          
        <!-- v-if EvalueateFunction is parameter sum -->
        <div v-if="isEvaluateParameter" class="text-h8 text-bold q-mt-md">
          {{ $t('parameter') }}
        </div>
        <mosta-tree-select
          v-if="isEvaluateParameter"
          ref="param_select"
          :display-text="parameterModelStr"
          :placeholder="$t('select_parameter')"
          :content-max-height="300"
          @update:search="(val) => { filter_string = val; }"
          @keyDown="$refs.rec_selector_base && $refs.rec_selector_base.trackArrowDown(1)"
          @keyUp="$refs.rec_selector_base && $refs.rec_selector_base.trackArrowUp(1)"
          @keyEnter="$refs.rec_selector_base && $refs.rec_selector_base.selectCurrent()"
        >
          <recursive-selector
            ref="rec_selector_base"
            :elements="(theModel as ClientProjectLinearTypes).Parameters"
            :take-ids-only="true"
            :allow-multiple="false"
            :element-type="1"
            v-model="curveParameterModel"
            return-type="chain"
            :nested-key-name="NestedKeyName"
            parameter-type-name="Type"
            :filter-out-list="[3,6,7]"
            :filter-string="filter_string"
          />
        </mosta-tree-select>

        <MostaSelect
          v-if="!isEvaluateFormulas && !isReportSegment"
          v-model="sectorAnalyzedModel"
          class="q-mb-xs"
          option-label="Name"
          :label="$t('analyzed_element')"
          :options="genstore.analyzedListForChart"
        />

        <!-- person workers -->
        <div class="row" style="margin-left: -5px;" v-if="[9, 12, 15].includes((theModel as any)?.Descriptor)">
          <q-btn
            ref="workerMenuTrigger"
            class="q-my-none q-pa-none text-left text-md" flat no-caps align="left" size="13px" color="grey-8"
            icon="add"
            :aria-label="$t('choose_user')"
            @click="workerMenuVisible = !workerMenuVisible"
          >
            <q-tooltip :delay="300" anchor="top middle" self="center middle">
              {{ $t('choose_user') }}
            </q-tooltip>
          </q-btn>
          <selector-popup
            v-model:visible="workerMenuVisible"
            :trigger-el="$refs.workerMenuTrigger"
            wide-layout
          >
            <aux-selector :model-type="6" :input-index="0"
              :limit-height-to-container="true"
              :allow-multiple="true"
              v-model="curveWorkersModel" @selectorClosed="workerMenuVisible = false" />
          </selector-popup>
          <div class="text-h8 q-ml-sm" style="font-weight: 500; margin-top: 7px;">
            {{ $t('users') }}
          </div>
        </div>
        <variable-display-list class="q-mb-sm" v-if="[9, 12, 15].includes((theModel as any)?.Descriptor)"
          v-model="curveWorkersModel" @itemDeleted="deleteWorker" :modelType="1" :modelCategory="0" />

        <!-- job status -->
        <div v-if="(theModel as JobLinearChartSettings)?.Descriptor === 6">
          <q-select
            flat
            v-model="jobStatusModel"
            class="no-select-border q-mb-xs"
            hide-bottom-space
            multiple
            :bottom-slots="false"
            dense outlined
            clearable
            input-debounce="500"
            :label="!jobStatusModel || !jobStatusModel.length ? $t('statuses') : ''"
            ref="job_status_select"
            :hide-dropdown-icon="false"
            :options="jobStatusOptions"
          >
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-grey">
                  {{ $t('no_data_found') }}
                </q-item-section>
              </q-item>
            </template>      
          </q-select>
        </div>

        <!-- if Descriptor === ProjectLinearChart -->
        <q-select
          flat
          v-if="(curveModel as ClientProjectLinearCurveTypes)?.EvaluateFunction === 0 && (theModel as ClientProjectLinearTypes)?.Descriptor === 3"
          v-model="curveStageModel"
          use-input
          fill-input
          multiple outlined
          use-chips
          input-debounce="500"
          :label="$t('stages')"
          :options="(theModel as ProjectLinearChartSettings).Stages?.map(el => { return { value: el.Id, label: el.Name }})"
        >
          <template v-slot:no-option>
            <q-item>
              <q-item-section class="text-grey">
                {{ $t('no_data_found') }}
              </q-item-section>
            </q-item>
          </template>
          
        </q-select>

        <div class="text-h8 text-bold q-mb-none">
          {{ $t('color') }}
        </div>
        <div
          class="row justify-right q-pa-xs text-overline cursor-pointer"
          outlined
          :style="`background-color: ${curveColorModel}; width: 100%; border-radius: 4px; color: white;`"
        >
          {{ curveColorModel }}
          <div class="col text-right">
            <q-icon name="colorize" size="sm" />
            <q-popup-proxy cover transition-show="scale" transition-hide="scale">
              <q-color v-model="curveColorModel" />
            </q-popup-proxy>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { AnalyticsChartIdentity, LinearChartCurveSettingsBase, LinearChartSettingsBase } from '@/models/interfaces/entity-settings/charts/charts';
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { AnalyticsV2ChartType, AnalyticsV2ElementType, ChartSettingsBaseDescriptor, ClientProjectChartFunction, FileLinearChartFactFlags, JobStatusFilterFlags, LinearChartFunction, LinearChartStepType, PersonLinearChartFactFlags, StorageItemChartEvaluateFunction, StorageItemMovementChartEvaluateFunction } from '@/models/etc/types';
import EnumDisplayList from '@/components/common/basic/EnumDisplayList.vue';
import DateRangeDisplayItem from '@/components/common/basic/data-components/DateRangeDisplayItem.vue';
import RecursiveSelector from '@/components/common/basic/recursive-selector/refactored/RecursiveSelector.vue';
import { ClientProjectLinearCurveTypes, ClientProjectLinearTypes, graphSettingsStore, LinearTypes } from '@/store/graph';
import { generalStore } from '@/store/general';
import { rootStore } from '@/store/index';
import { colors } from 'quasar'
import SelectorPopup from '@/components/common/aux-selector/SelectorPopup.vue';
import { arrayEquals, findRecursive } from '@/models/helpers/recursive';
import { ProjectLinearChartSettings } from '@/models/interfaces/entity-settings/charts/project-charts';
import { JobLinearChartCurveSettings } from '@/models/interfaces/entity-settings/charts/job-charts';
import { PersonLinearChartCurveSettings, PersonLinearChartSettings } from '@/models/interfaces/entity-settings/charts/person-charts';
import VariableDisplayList from '@/components/common/basic/VariableDisplayList.vue';
import { FileLinearChartSettings } from '@/models/interfaces/entity-settings/charts/file-charts';
import { reactive } from 'vue';
import { CommonEntity } from '@/models/interfaces/common';
import _ from 'lodash';
import FormulasList from '@/components/common/formulas-list/FormulasList.vue';
// import { storeToRefs } from 'pinia';
import { ChartValidationErrors } from '@/store/validation';
import ElementOptionsSettingsPanel from '../../common/settings-list/ElementOptionsSettingsPanel.vue';
import MostaSelect from '@/components/common/MostaSelect.vue';
import MostaTreeSelect from '@/components/common/MostaTreeSelect.vue';
const { lighten } = colors

const SCALE_LINEAR = process.env.VUE_APP_SCALE_LINEAR

@Component({
  name: 'LinearChartPanel',
  components: { EnumDisplayList, DateRangeDisplayItem, RecursiveSelector,
  VariableDisplayList, FormulasList, ElementOptionsSettingsPanel, SelectorPopup, MostaSelect, MostaTreeSelect }
})
export default class LinearChartPanel extends Vue {
  // v-model
  @Prop({ default: {} as LinearChartSettingsBase })
  modelValue: any | undefined
  @Emit("update:modelValue") emitModelValue(arg: LinearChartSettingsBase | undefined ) { return arg; }

  genstore: any | undefined = undefined
  graphstore: any | undefined = undefined

  isEditingMode: boolean = false
  tempName: string = ''
  filter_string: string = ''
  menus: any[] = reactive([])
  inputs: any[] = reactive([])
  dateInnerModel: any = reactive({})

  selectedId: number = 0
  params_model = []
  NestedKeyName = 'RecordFields'

  // graphstore: any
  datesIncorrect: string = ''
  stepIncorrect: string = ''

  set_menu: any = reactive({})
  workerMenuVisible: boolean = false

  @Watch('filter_string')
  // change focus from search field to list when up and down arrow keys are pressed
  focusPanel() {
    const that = this
    setTimeout(() => {
      that.$refs.rec_selector_base && (that.$refs.rec_selector_base as any).trackArrowDown(1, true)
    }, 100)
  }

  lightenColor(color: string) {
    return lighten(color, 40)
  }

  renameBtnClick(level: number) {
    this.tempName = this.curveModel[level].Name
    const that = this
    this.$nextTick(() => {
      setTimeout(() => {
        // @ts-ignore
        that.inputs[level].select();
      }, 100)
    })
  }

  created() {
    this.genstore = generalStore()
    this.graphstore = graphSettingsStore()
    // this.graphstore = storeToRefs(this.graphstore)
    this.dateInnerModel = reactive({
      StartDate: this.theModel?.DateRangeStart,
      EndDate: this.theModel?.DateRangeEnd,
      EndDateIsToday: this.theModel?.DateRangeEndIsToday,
    })
  }

  mounted() {
    this.selectedId = -1
  }

  get chartsValidationMutated() { return _.cloneDeep(this.graphstore?.getChartsValidation) }

  @Watch('chartsValidationMutated', { deep: true })
  updateIncorrect(val: any) {
    if (!this.theModel) {
      this.datesIncorrect = '';
      this.stepIncorrect = '';
      return;
    } else {
      this.datesIncorrect = val.find((el: any) => el.chartDescriptor === this.theModel?.Descriptor && 
        el.errorType === ChartValidationErrors.DATE_RANGE)?.errorText || ''
      this.stepIncorrect = val.find((el: any) => el.chartDescriptor === this.theModel?.Descriptor && 
        el.errorType === ChartValidationErrors.STEP_LENGTH)?.errorText || ''
    }
  }

  addNewGElement() {
    const request: AnalyticsChartIdentity = {
      ChartId: this.genstore.selectedItem.Id,
      ChartType: this.genstore.selectedItem.elType === 2 ? AnalyticsV2ChartType.Linear : 
        (this.genstore.selectedItem.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
      Destination: this.genstore.selectedItem.DestType
    }
    this.graphstore.addGraphElement(request)
  }

  async deleteGElement(del_index: number) {
    const request: AnalyticsChartIdentity = {
      ChartId: this.genstore.selectedItem.Id,
      ChartType: this.genstore.selectedItem.elType === 2 ? AnalyticsV2ChartType.Linear : 
        (this.genstore.selectedItem.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
      Destination: this.genstore.selectedItem.DestType
    }
    const deleting_current = (del_index === this.curve_index)
    await this.graphstore.removeGraphElement(this.curveModel[del_index].Id, this.curveModel[del_index].Descriptor, request)
   
    const that = this
    this.$nextTick(() => {
      if (deleting_current) {
        if (del_index > 0) {
          that.selectedId = that.curveModel[del_index-1].Id
        } else if (that.curveModel.length > 0) {
          that.selectedId = that.curveModel[0].Id
        } else {
          that.selectedId = -1
        }
      }
    })
  }

  get curve_index() {
    return this.curveModel.findIndex((el: any) => (el.Id === this.selectedId))
  }

  get scaleLinear(): boolean {
    // console.log(SCALE_LINEAR)
    return (SCALE_LINEAR == 1)
  }

  cloneGElement(cl_index: number) {
    const request: AnalyticsChartIdentity = {
      ChartId: this.genstore.selectedItem.Id,
      ChartType: this.genstore.selectedItem.elType === 2 ? AnalyticsV2ChartType.Linear : 
        (this.genstore.selectedItem.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
      Destination: this.genstore.selectedItem.DestType
    }
    this.graphstore.cloneGraphElement(this.curveModel[cl_index].Id, this.curveModel[cl_index].Descriptor, request)
  }

  async moveFormulaItem(params: any) {
    if (this.graphstore && this.curveModel && this.curveModel[this.curve_index]) {
      this.graphstore.changeChartFormulaPosition(params.dragParams.innerElementId, this.curveModel[this.curve_index].Descriptor,
      this.curveModel[this.curve_index].Id, params.new_pos)
    }
  }

  renameGElement(ren_index: number) {
    if (this.tempName.length <= 0) { return }
    this.menus[ren_index] && (this.menus[ren_index].hide());
    
    const request: AnalyticsChartIdentity = {
      ChartId: this.genstore.selectedItem.Id,
      ChartType: this.genstore.selectedItem.elType === 2 ? AnalyticsV2ChartType.Linear : 
        (this.genstore.selectedItem.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
      Destination: this.genstore.selectedItem.DestType
    }
    this.graphstore.renameGraphElement(this.curveModel[ren_index].Id, this.tempName, this.curveModel[ren_index].Descriptor, request)
  }

  // common linear chart model
  get theModel(): LinearChartSettingsBase | undefined {
    return this.modelValue
  }

  set theModel(val: LinearChartSettingsBase | undefined) {
    this.emitModelValue(val)
  }

  get isEvaluateFormulas() {
    return (this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ClientLinearChart || 
           this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ProjectLinearChart) && 
           (this.curveModel[this.curve_index] as ClientProjectLinearCurveTypes)
      .EvaluateFunction === ClientProjectChartFunction.Formulas
  }

  // В сегменте отчётов график лежит как ссылка: его реальное окружение (папка/выборка) там
  // неизвестно, поэтому список анализируемых элементов собрать не из чего. Настраивается
  // только в родном сегменте графика.
  get isReportSegment(): boolean {
    return rootStore().generalMode === AnalyticsV2ElementType.Report
  }

  get isEvaluateParameter() {
    return (this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ClientLinearChart || 
           this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ProjectLinearChart) && 
           (this.curveModel[this.curve_index] as ClientProjectLinearCurveTypes).EvaluateFunction === 1
  }

  get isLinearChart() {
    return this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ClientLinearChart || 
           this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ProjectLinearChart ||
           this.theModel?.Descriptor === ChartSettingsBaseDescriptor.StorageItemLinearChart ||
           this.theModel?.Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementLinearChart
  }

  get hasAnalyticElementOptions(): boolean {
    if (!this.theModel) {
      return false
    }
    return this.theModel?.Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementLinearChart
  }

  // step function
  set stepModel(val: any) {
    this.theModel && (this.theModel.StepType = val)
  }

  get stepModel(): any {
    return [this.theModel?.StepType]
  }

  // step function
  set stepValueModel(val: any) {
    this.theModel && (this.theModel.StepLength = val)
  }

  get stepValueModel(): any {
    return this.theModel?.StepLength
  }

  // linear chart curves
  get curveModel(): any {
    if (!this.theModel) { return [] }

    const that = this;
    this.$nextTick(() => {
      setTimeout(() => {
        if (that.selectedId <= 0 && that.curveModel && that.curveModel.length) {
          that.selectedId = that.curveModel[0].Id
        }
      }, 100)
    })

    return (this.theModel as LinearTypes).Curves
  }

  set curveModel(val: any) {
    (this.theModel as LinearTypes).Curves = [...val]
  }

  // parameters model for ClientLinearChartSettings or ProjectLinearChartSettings
  get curveParameterModel(): any {
    return [this.curveModel[this.curve_index]?.ParameterLink?.IdChain]
  }

  hideSetMenu() {
    this.workerMenuVisible = false;
  }

  // display selected parameter name
  get parameterModelStr(): string {
    const that = this
    if (!this.curveModel ||
    !this.curveModel[this.curve_index] ||
    !this.curveModel[this.curve_index].ParameterLink ||
    !this.curveModel[this.curve_index].ParameterLink.IdChain ||
    !this.curveModel[this.curve_index].ParameterLink.IdChain.length) {
      return this.$t('no_parameter_selected')
    }

    const param = findRecursive(function a(element: any, el_to_find: any): any | undefined {
      if (!element) return undefined

      if (arrayEquals([ ...el_to_find.chain, element.Id ], el_to_find.el_to_find)) {
        return element;
      }
      if (element[that.NestedKeyName]) {
        return findRecursive(a, {
          chain: [ ...el_to_find.chain, element.Id ], 
          el_to_find: el_to_find.el_to_find
        }, element[that.NestedKeyName])
      }
      return undefined
    }, { chain: [], el_to_find: this.curveModel[this.curve_index].ParameterLink.IdChain }, 
    (this.theModel as ClientProjectLinearTypes).Parameters)

    return param ? param.Name : this.$t('no_parameter_selected')
  }

  set curveParameterModel(val: any) {
    const next = val?.[0];
    const current = this.curveModel[this.curve_index]?.ParameterLink?.IdChain;
    if (this.curveModel && this.curveModel[this.curve_index]) {
      this.curveModel[this.curve_index].ParameterLink = { IdChain: next }
    }
    if (!arrayEquals(current || [], next || [])) {
      (this.$refs.param_select as any)?.close?.();
    }
  }

  // stages model for ProjectLinearChartSettings
  get curveStageModel(): any {
    if (!this.curveModel ||
    !this.curveModel[this.curve_index] ||
    !this.curveModel[this.curve_index].StageIds) {
      return []
    }

    // return [this.curveModel[this.curve_index]?.StageIds]
    return (this.theModel as ProjectLinearChartSettings).Stages.filter(element => {
      return (this.curveModel[this.curve_index].StageIds.includes(element.Id))
    }).map(el => { return { value: el.Id, label: el.Name }})
  }

  set curveStageModel(val: any) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      this.curveModel[this.curve_index].StageIds = val ? val.map((el: any) => el.value) : []
    }
  }

  // seдected workers model
  get curveWorkersModel(): any {
    if (!this.curveModel ||
    !this.curveModel[this.curve_index] ||
    !this.curveModel[this.curve_index].SelectedWorkers) {
      return []
    }

    // console.log(this.curveModel[this.curve_index])
    return (this.curveModel[this.curve_index] as PersonLinearChartCurveSettings).SelectedWorkers
  }

  set curveWorkersModel(val: any) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      this.curveModel[this.curve_index].SelectedWorkers = val ? val.map((el: any) => el.Entity) : []
    }
  }

  deleteWorker(d_index: number) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      this.curveModel[this.curve_index].SelectedWorkers.splice(d_index, 1)
    }
  }

  // curve color model
  get curveColorModel(): string {
    if (!this.curveModel ||
    !this.curveModel[this.curve_index]) {
      return '#000000'
    }

    return (this.curveModel[this.curve_index] as LinearChartCurveSettingsBase).Color
  }

  set curveColorModel(val: string) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      (this.curveModel[this.curve_index] as LinearChartCurveSettingsBase).Color = val
    }
  }

  // linear function
  set curveLinearModel(val: any) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      (this.curveModel[this.curve_index] as LinearChartCurveSettingsBase).LinearFunction = val
    }
  }

  get curveLinearModel(): any {
    if (!this.curveModel || !this.curveModel[this.curve_index]) { return [] }
    return [(this.curveModel[this.curve_index] as LinearChartCurveSettingsBase).LinearFunction]
  }

  // evaluate function for ClientLinearChartSettings or ProjectLinearChartSettings
  set curveEvaluateModel(val: any) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      (this.curveModel[this.curve_index] as ClientProjectLinearCurveTypes).EvaluateFunction = val
    }
  }

  get curveEvaluateModel(): any {
    if (!this.curveModel || !this.curveModel[this.curve_index]) { return [] }
    return [(this.curveModel[this.curve_index] as ClientProjectLinearCurveTypes).EvaluateFunction]
  }

  // get dateModel(): any {
  //   return {
  //     Positive: true,
  //     StartDate: this.theModel?.DateRangeStart,
  //     EndDate: this.theModel?.DateRangeEnd,
  //     EndDateIsToday: this.theModel?.DateRangeEndIsToday,
  //   }
  // }

  // set dateModel(val: any) {
  get dateModelMutated() { return _.cloneDeep(this.dateInnerModel)}
  @Watch('dateModelMutated', { deep: true })
  dateModelWatcher(val: any, old_val: any) {
    if (_.isEqual(val, old_val)) { return }
    console.log('set date')
    console.log(val)
    if (this.theModel) {
      this.theModel.DateRangeStart = val.StartDate
      this.theModel.DateRangeEnd = val.EndDate
      this.theModel.DateRangeEndIsToday = val.EndDateIsToday
    }
  }

  get evaluateToList() {
    switch (this.theModel?.Descriptor) {
      case ChartSettingsBaseDescriptor.StorageItemLinearChart:
        return Object.values(StorageItemChartEvaluateFunction).filter((v) => !isNaN(Number(v)));
      case ChartSettingsBaseDescriptor.StorageItemMovementLinearChart:
        return Object.values(StorageItemMovementChartEvaluateFunction).filter((v) => !isNaN(Number(v)));
      default:
        return Object.values(ClientProjectChartFunction).filter((v) => !isNaN(Number(v)));
    }
  }

  EvaluateFunctionToStr(val: number): string {
    switch (this.theModel?.Descriptor) {
      case ChartSettingsBaseDescriptor.StorageItemLinearChart:
        switch(val) {
          case StorageItemChartEvaluateFunction.ItemsCount:
            return this.$t('unique_items')
          case StorageItemChartEvaluateFunction.ItemsInStockQuantity:
            return this.$t('warehouse_items')
          case StorageItemChartEvaluateFunction.ItemsInStockTotalAmount:
            return this.$t('warehouse_value')
          case StorageItemChartEvaluateFunction.FinancialBurden:
            return this.$t('financial_load')
          default:
            return ""+val
        }
      case ChartSettingsBaseDescriptor.StorageItemMovementLinearChart:
        switch(val) {
          case StorageItemMovementChartEvaluateFunction.ItemsQuantity:
            return this.$t('count_type')
          case StorageItemMovementChartEvaluateFunction.MovementsCount:
            return this.$t('actions_count')
          case StorageItemMovementChartEvaluateFunction.TotalAmount:
          return this.$t('totalamount_type')
          default:
            return ""+val
        }
      default:
        switch(val) {
          case ClientProjectChartFunction.Count:
            return this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ClientLinearChart ? 
            this.$t('clients_count') : this.$t('projects_on_stage_count')
          case ClientProjectChartFunction.ParameterSum:
            return this.theModel?.Descriptor === ChartSettingsBaseDescriptor.ClientLinearChart ? 
            this.$t('clients_parameter_sum') : this.$t('projects_parameter_sum')
          case ClientProjectChartFunction.Formulas:
          return this.$t('formulas_label')
          default:
            return ""+val
        }
    }
  }

  // analyzed element
  set sectorAnalyzedModel(val: any) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      this.curveModel[this.curve_index].AnalyzedElementIdentity = val?.Identity ?? null

      if (val?.Name) {
        this.tempName = val.Name;
        let add_i = 0;
        while (this.curveModel.findIndex((s: CommonEntity) => s.Name === this.tempName) > 0) {
          add_i++;
          this.tempName = val.Name + add_i;
        }
        this.renameGElement(this.curve_index);
      }
    }
  }

  get sectorAnalyzedModel(): any {
    if (!this.theModel || !this.genstore.analyzedListForChart) return undefined
    const identity = this.curveModel[this.curve_index]?.AnalyzedElementIdentity
    if (!identity) return undefined
    const found_el = this.genstore.analyzedListForChart.find((el: any) => {
      const i = el?.Identity
      return i &&
        i.ElementId === identity.ElementId &&
        i.Kind === identity.Kind &&
        (i.Source ?? null) === (identity.Source ?? null) &&
        i.Destination === identity.Destination
    })
    return found_el
  }
  
  // linear function
  get linearFunctionToList() {
    return Object.values(LinearChartFunction).filter((v) => !isNaN(Number(v)))
  }

  LinearFunctionToStr(val: number): string {
    switch(val) {
      case LinearChartFunction.Absolute:
        return this.$t('function_absolute')
      case LinearChartFunction.Incremental:
        return this.$t('function_incremental')
      default:
        return ""+val
    }
  }

  get linearStepToList() {
    console.log(this.genstore.selectCurrent)
    return Object.values(LinearChartStepType).filter((v) => !isNaN(Number(v)) && 
    (Number(v) !== 5 || this.genstore.selectedItem && this.genstore.selectedItem.DestType === 6))
  }

  LinearStepToStr(val: number): string {
    switch(val) {
      case LinearChartStepType.Hour:
        return this.$t('hour')
      case LinearChartStepType.Day:
        return this.$t('day')
      case LinearChartStepType.Month:
        return this.$t('month')
      case LinearChartStepType.Quarter:
        return this.$t('quarter')
      case LinearChartStepType.Year:
        return this.$t('year')
      case LinearChartStepType.Week:
        return this.$t('week')
      default:
        return ""+val
    }
  }

  // TODO - Translase
    // width function
    get elementWidthOptions() {
      // multiplier to base size
      return [{
        label: 'x0.25',
        value: 7,
        multiplier: 0.25
      }, {
        label: 'x0.5',
        value: 1,
        multiplier: 0.5
      }, {
        label: 'x0.75',
        value: 2,
        multiplier: 0.75
      }, {
        label: 'x1',
        value: 0,
        multiplier: 1
      }, {
        label: 'x1.25',
        value: 3, // 4 - 1.25
        multiplier: 1.25
      }, {
        label: 'x1.5',
        value: 4,
        multiplier: 1.5
      }, {
        label: 'x1.75',
        value: 5,
        multiplier: 1.75
      }, {
        label: 'x2',
        value: 6,
        multiplier: 2
      }]
    }

    // width function
    get widthOptions() {
      // multiplier to 100% = (value + 1) - value*0.5
      return [{
        label: this.$t('quarter_screen_width'),
        value: 5,
        multiplier: 0.25
      }, {
        label: this.$t('half_screen_width'),
        value: 6,
        multiplier: 0.5
      }, {
        label: this.$t('screen_0_75'),
        value: 7,
        multiplier: 0.75
      }, {
        label: this.$t('screen_width'),
        value: 0,
        multiplier: 1
      }, {
        label: this.$t('screen_1_25'),
        value: 8,
        multiplier: 1.25
      }, {
        label: this.$t('screenup_width'),
        value: 1,
        multiplier: 1.5
      }, {
        label: this.$t('screen_1_75'),
        value: 9,
        multiplier: 1.75
      }, {
        label: this.$t('double_screen_width'),
        value: 2,
        multiplier: 2
      }, {
        label: this.$t('doubleup_screen_width'),
        value: 3,
        multiplier: 2.5
      }, {
        label: this.$t('triple_screen_width'),
        value: 4,
        multiplier: 3
      }]
    }

  set widthModel(val: any) {
    if (!this.theModel || !(this.theModel as LinearChartSettingsBase)) { return }
    this.theModel && ((this.theModel as LinearChartSettingsBase).ChartWidth = val.value)
  }

  get widthModel(): any {
    return this.widthOptions.find(opt => opt.value === (this.theModel as LinearChartSettingsBase)?.ChartWidth)
  }

  set elementWidthModel(val: any) {
    if (!this.theModel || !(this.theModel as LinearChartSettingsBase)) { return }
    this.theModel && ((this.theModel as LinearChartSettingsBase).ChartElementWidth = val.value)
  }

  get elementWidthModel(): any {
    return this.elementWidthOptions.find(opt => opt.value === (this.theModel as LinearChartSettingsBase)?.ChartElementWidth)
  }

  // job status model
  // job status flags filter
  jobStatusName(arg: number): string {
    switch (arg) {
      case JobStatusFilterFlags.Completed:
        return this.$t('job_done')
      case JobStatusFilterFlags.Lost:
        return this.$t('job_expired')
      case JobStatusFilterFlags.Standard:
        return this.$t('job_is_record')
      case JobStatusFilterFlags.WaitingForCheck:
        return this.$t('job_waiting')
      case JobStatusFilterFlags.Working:
        return this.$t('job_in_progress')
      case JobStatusFilterFlags.Nothing:
      default:
        return this.$t('filter_empty')
    }
  }

  get jobStatusOptions() {
    return Object.values(JobStatusFilterFlags)
      .filter((v) => !isNaN(Number(v)) && Number(v) > 0)
      .map((vm) => {
        return {
          label: this.jobStatusName(Number(vm)),
          value: Number(vm)
        }
      })
  }

  get jobStatusModel() {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      return this.jobStatusOptions.filter((mask: any) => {
        return ((this.curveModel[this.curve_index] as JobLinearChartCurveSettings).StatusFlags & mask.value) !== 0
      })
    }
    return []
  }

  set jobStatusModel(mask: any[]) {
    if (this.curveModel && this.curveModel[this.curve_index]) {
      (this.curveModel[this.curve_index] as JobLinearChartCurveSettings).StatusFlags = 0
      if (!mask || !mask.length) { return }

      mask.forEach(mask => {
        (this.curveModel[this.curve_index] as JobLinearChartCurveSettings).StatusFlags |= mask.value;
      })
      
      // console.log((this.curveModel[this.curve_index] as JobLinearChartCurveSettings).StatusFlags)
    }
  }

  // person fact flats model
  // person fact flags filter
  factFlagsNamePerson(arg: number): string {
    switch (arg) {
      case PersonLinearChartFactFlags.PersonAttachedToProject:
        return this.$t('contacts_projects_attachment')
      case PersonLinearChartFactFlags.PersonChanged:
        return this.$t('contacts_editing')
      case PersonLinearChartFactFlags.PersonCreated:
        return this.$t('contacts_creation')
      case PersonLinearChartFactFlags.PersonExists:
        return this.$t('presence')
        // return this.$t('contacts_jobs_attachment')
      default:
        return this.$t('filter_empty')
    }
  }

  get factFlagsOptionsPerson() {
    return Object.values(PersonLinearChartFactFlags)
      .filter((v) => !isNaN(Number(v)) && Number(v) > 0)
      .map((vm) => {
        return {
          label: this.factFlagsNamePerson(Number(vm)),
          value: Number(vm)
        }
      })
  }

  get factFlagsModelPerson() {
    if (this.theModel) {
      return this.factFlagsOptionsPerson.filter((mask: any) => {
        return ((this.theModel as PersonLinearChartSettings).FactFlags & mask.value) !== 0
      })
    }
    return []
  }

  set factFlagsModelPerson(mask: any[]) {
    if (this.theModel) {
      (this.theModel as PersonLinearChartSettings).FactFlags = 0
      if (!mask || !mask.length) { return }

      mask.forEach(mask => {
        (this.theModel as PersonLinearChartSettings).FactFlags |= mask.value;
      })
      
      console.log((this.theModel as PersonLinearChartSettings).FactFlags)
    }
  }

  // file fact flats model
  // file fact flags filter
  factFlagsNameFile(arg: number): string {
    switch (arg) {
      case FileLinearChartFactFlags.FileExists:
        return this.$t('presence')
      case FileLinearChartFactFlags.FileChanged:
        return this.$t('files_editing')
      case FileLinearChartFactFlags.FileCreated:
        return this.$t('files_creation')
      default:
        return this.$t('filter_empty')
    }
  }

  get factFlagsOptionsFile() {
    return Object.values(FileLinearChartFactFlags)
      .filter((v) => !isNaN(Number(v)) && Number(v) > 0)
      .map((vm) => {
        return {
          label: this.factFlagsNameFile(Number(vm)),
          value: Number(vm)
        }
      })
  }

  get factFlagsModelFile() {
    if (this.theModel) {
      return this.factFlagsOptionsFile.filter((mask: any) => {
        return ((this.theModel as FileLinearChartSettings).FactFlags & mask.value) !== 0
      })
    }
    return []
  }

  set factFlagsModelFile(mask: any[]) {
    if (this.theModel) {
      (this.theModel as FileLinearChartSettings).FactFlags = 0
      if (!mask || !mask.length) { return }

      mask.forEach(mask => {
        (this.theModel as FileLinearChartSettings).FactFlags |= mask.value;
      })
      
      console.log((this.theModel as FileLinearChartSettings).FactFlags)
    }
  }
}
</script>

<style lang="scss">
.q-btn__content > span {
  text-align: left !important;
}
</style>